import { h } from "preact";
import { useEffect } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { format } from "date-fns";

import AppFooter from "../components/AppFooter.jsx";
import AppHeader from "../components/AppHeader.jsx";
import AppFocusLayout from "../components/AppFocusLayout.jsx";
import BackButton from "../components/BackButton.jsx";
import ShareButton from "../components/ShareButton.jsx";
import WhitepaperErrorContent from "../components/WhitepaperErrorContent.jsx";
import i18n from "../i18n.js";
import { fetchWhitepapers, setLoading } from "../store";
import { renderHtml } from "../helpers.js";
import WhitepaperLearnMoreTeaser from "../components/WhitepaperLearnMoreTeaser.jsx";
import Button from "../components/Button.jsx";
import Icon from "../components/Icon.jsx";

export default function WhitepaperShowPage() {
  const { whitepaperId } = useParams();
  const dispatch = useDispatch();
  const whitepaper = useSelector((state) =>
    (state.whitepaper.items || []).find((wp) => wp.id == whitepaperId)
  );
  const similarPosts = useSelector((state) => {
    const items = state.whitepaper.items || [];
    const post = items.find((wp) => wp.id == whitepaperId);
    if (post) {
      return post.similar
        .map((tag) => items.find((wp) => wp.tags.includes(tag)))
        .filter((x) => x);
    }
    return [];
  });
  const isErrornous = useSelector((state) => state.whitepaper.loadingError);
  const isLoading = useSelector((state) => state.whitepaper.loading);

  useEffect(() => {
    dispatch(fetchWhitepapers());
  }, []);

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [whitepaperId]);

  if (isErrornous) {
    return (
      <AppFocusLayout footer={<AppFooter />}>
        <WhitepaperErrorContent />
      </AppFocusLayout>
    );
  }

  if (!whitepaper) {
    return null;
  }

  return (
    <AppFocusLayout
      closerAction={
        <Button text to={`/whitepaper`}>
          <Icon>close</Icon>
        </Button>
      }
      title={i18n.t("pages.titles.whitepaper")}
      header={<AppHeader />}
      footer={<AppFooter />}
    >
      <div class="row">
        <div class="col s12 l8 offset-l2">
          <div class="intro">
            <span class="tag tag--default">
              <span class="tag__content">{whitepaper.tags}</span>
            </span>
            <h1 class="t-h4 t-h4--margin">{whitepaper.title}</h1>
            <p class="t-body4 color-shades-grey mt-32 mb-32">
              {i18n.t("whitepaper.read", { time: whitepaper.readTime })}
            </p>
            <p class="t-body1 color-shades-charcoal">{whitepaper.excerpt} </p>
            <div>
              <span class="t-body4">{whitepaper.author}</span>
              <span class="t-body4 color-shades-grey ml-24">
                {i18n.t("whitepaper.last_updated", {
                  date: format(new Date(whitepaper.lastUpdatedAt), "PPP"),
                })}
              </span>
            </div>
            <img src={whitepaper.image} class="whitepaper__main-image" />
          </div>
          {renderHtml(whitepaper.body, "div", "markdown-body whitepaper__body")}
          {similarPosts.length > 0 && (
            <div>
              <h3 class="t-h5 t-h5--margin">
                {i18n.t("whitepaper.read_more")}
              </h3>
              {similarPosts.map((whitepaper) => (
                <WhitepaperLearnMoreTeaser whitepaper={whitepaper} />
              ))}
            </div>
          )}
          <p class="share-buttons mb-96 mt-96">
            <ShareButton
              type="facebook"
              url={whitepaper.url}
              title={whitepaper.title}
            />
            <ShareButton
              type="twitter"
              url={whitepaper.url}
              title={whitepaper.title}
            />
            <ShareButton
              type="linkedin"
              url={whitepaper.url}
              title={whitepaper.title}
            />
          </p>
        </div>
      </div>
    </AppFocusLayout>
  );
}
